import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'

import type { I18n, Person, ProjectCategory, TaskPriority, TaskStatus } from '../types'

/**
 * Ce qu'on cherche, à un instant donné.
 *
 * Tout est en chaînes, y compris les dates : c'est la forme qu'ont les champs
 * du formulaire, et convertir dans les deux sens à chaque frappe ferait perdre
 * une saisie en cours (« 2026-1 » n'est pas une date, mais c'en est le début).
 */
export interface TaskFilterState {
  q: string
  category: string
  owner: string
  assignee: string
  author: string
  priority: string
  status: string
  dueFrom: string
  dueTo: string
  late: boolean
  unassigned: boolean
}

export const EMPTY_FILTERS: TaskFilterState = {
  q: '',
  category: '',
  owner: '',
  assignee: '',
  author: '',
  priority: '',
  status: '',
  dueFrom: '',
  dueTo: '',
  late: false,
  unassigned: false,
}

/** Combien de critères sont posés — le chiffre affiché sur le bouton. */
export function activeCount(filters: TaskFilterState): number {
  return Object.entries(filters).filter(([, value]) =>
    typeof value === 'boolean' ? value : value.trim() !== '',
  ).length
}

/**
 * La chaîne de requête envoyée au serveur.
 *
 * **Les filtres s'appliquent en base, jamais sur la page déjà chargée.** Trier
 * côté navigateur donnerait un résultat juste tant que tout tient en mémoire,
 * puis faux le jour où ce n'est plus le cas — sans que rien ne le signale.
 */
export function toQuery(filters: TaskFilterState): string {
  const params = new URLSearchParams()
  if (filters.q.trim()) params.set('q', filters.q.trim())
  // La valeur sentinelle « none » vaut « sans chantier » : c'est une absence,
  // pas une catégorie, et elle n'a donc pas d'identifiant à envoyer.
  if (filters.category === 'none') params.set('uncategorised', 'true')
  else if (filters.category) params.set('category_id', filters.category)
  if (filters.owner) params.set('owner_user_id', filters.owner)
  if (filters.assignee) params.set('assignee_user_id', filters.assignee)
  if (filters.author) params.set('created_by', filters.author)
  if (filters.priority) params.set('priority_id', filters.priority)
  if (filters.status) params.set('status_id', filters.status)
  // `URLSearchParams` encode le « + » du décalage horaire : sans cela il
  // arriverait au serveur comme une espace, et l'horodatage serait refusé.
  if (filters.dueFrom) params.set('due_from', `${filters.dueFrom}T00:00:00Z`)
  if (filters.dueTo) params.set('due_to', `${filters.dueTo}T23:59:59Z`)
  if (filters.late) params.set('overdue', 'true')
  if (filters.unassigned) params.set('unassigned', 'true')
  return params.toString()
}

/**
 * Les filtres — en ligne sur grand écran, en tiroir sur téléphone.
 *
 * Huit critères empilés mangeraient l'écran d'un téléphone avant qu'on ait vu
 * la première tâche. Ils passent donc derrière un bouton qui **annonce combien
 * de critères sont actifs** : un filtre qu'on a oublié d'enlever est la
 * première cause de « mes tâches ont disparu ».
 */
export function TaskFilters({
  filters,
  onChange,
  categories,
  people,
  priorities,
  statuses,
  label,
  total,
}: {
  filters: TaskFilterState
  onChange: (next: TaskFilterState) => void
  categories: ProjectCategory[]
  people: Person[]
  priorities: TaskPriority[]
  statuses: TaskStatus[]
  label: (value: I18n) => string
  total: number
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const count = activeCount(filters)

  // Le tiroir se ferme avec la touche Échap : c'est le réflexe, et l'ignorer
  // enferme sur un téléphone où le bouton de fermeture peut être sous le pouce.
  useEffect(() => {
    if (!open) return
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [open])

  const set = <K extends keyof TaskFilterState>(key: K, value: TaskFilterState[K]) =>
    onChange({ ...filters, [key]: value })

  const fields = (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field label={t('admin.project.filterCategory')}>
        <Select
          value={filters.category}
          onChange={(value) => set('category', value)}
          placeholder={t('admin.project.allCategories')}
          options={[
            { value: 'none', label: t('admin.project.uncategorised') },
            ...categories.map((row) => ({ value: row.id, label: label(row.name_i18n) })),
          ]}
        />
      </Field>

      <Field label={t('admin.project.filterStatus')}>
        <Select
          value={filters.status}
          onChange={(value) => set('status', value)}
          placeholder={t('admin.project.allStatuses')}
          options={statuses.map((row) => ({ value: row.id, label: label(row.name_i18n) }))}
        />
      </Field>

      <Field label={t('admin.project.filterPriority')}>
        <Select
          value={filters.priority}
          onChange={(value) => set('priority', value)}
          placeholder={t('admin.project.allPriorities')}
          options={priorities.map((row) => ({ value: row.id, label: label(row.name_i18n) }))}
        />
      </Field>

      <Field label={t('admin.project.filterOwner')}>
        <Select
          value={filters.owner}
          onChange={(value) => set('owner', value)}
          placeholder={t('admin.project.allOwners')}
          options={people.map((row) => ({ value: row.id, label: row.label }))}
        />
      </Field>

      <Field label={t('admin.project.filterAssignee')}>
        <Select
          value={filters.assignee}
          onChange={(value) => set('assignee', value)}
          placeholder={t('admin.project.anyAssignee')}
          options={people.map((row) => ({ value: row.id, label: row.label }))}
        />
      </Field>

      <Field label={t('admin.project.filterAuthor')}>
        <Select
          value={filters.author}
          onChange={(value) => set('author', value)}
          placeholder={t('admin.project.anyAuthor')}
          options={people.map((row) => ({ value: row.id, label: row.label }))}
        />
      </Field>

      <Field label={t('admin.project.filterDueFrom')}>
        <input
          type="date"
          value={filters.dueFrom}
          onChange={(event) => set('dueFrom', event.target.value)}
          className={INPUT}
        />
      </Field>

      <Field label={t('admin.project.filterDueTo')}>
        <input
          type="date"
          value={filters.dueTo}
          onChange={(event) => set('dueTo', event.target.value)}
          className={INPUT}
        />
      </Field>

      <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-4">
        <Button
          size="sm"
          variant={filters.late ? 'ember' : 'outline'}
          onClick={() => set('late', !filters.late)}
        >
          {t('admin.project.onlyLate')}
        </Button>
        <Button
          size="sm"
          variant={filters.unassigned ? 'ember' : 'outline'}
          onClick={() => set('unassigned', !filters.unassigned)}
        >
          {t('admin.project.onlyUnassigned')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="mb-5 mt-6">
      <div className="flex flex-wrap items-center gap-2">
        {/* La recherche reste toujours visible : c'est le geste le plus
            fréquent, et l'enfouir sous un bouton le rendrait plus lent que de
            parcourir la liste. */}
        <input
          value={filters.q}
          onChange={(event) => set('q', event.target.value)}
          placeholder={t('admin.project.searchPlaceholder')}
          aria-label={t('admin.project.search')}
          className={cn(INPUT, 'min-w-0 flex-1 sm:max-w-xs')}
        />

        <Button
          size="sm"
          variant={count > 0 ? 'ember' : 'outline'}
          onClick={() => setOpen(true)}
          className="lg:hidden"
        >
          {count > 0
            ? t('admin.project.filtersWithCount', { count })
            : t('admin.project.filters')}
        </Button>

        {count > 0 ? (
          <Button size="sm" variant="ghost" onClick={() => onChange(EMPTY_FILTERS)}>
            {t('admin.project.resetFilters')}
          </Button>
        ) : null}

        <span className="ml-auto shrink-0 text-caption tabular-nums text-content-muted">
          {t('admin.project.matchCount', { count: total })}
        </span>
      </div>

      {/* Grand écran : les critères sont posés à plat, sans clic préalable. */}
      <div className="mt-4 hidden lg:block">{fields}</div>

      {/* Téléphone : un tiroir qui monte du bas, à portée de pouce. */}
      {open ? (
        <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/50 lg:hidden">
          <button
            type="button"
            aria-label={t('admin.project.close')}
            className="flex-1 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-modal
            className="max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t border-line bg-bg-base p-5"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="font-display text-heading-sm text-content-primary">
                {t('admin.project.filters')}
              </h2>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                {t('admin.project.close')}
              </Button>
            </div>

            {fields}

            <div className="mt-5 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setOpen(false)}>
                {t('admin.project.showResults', { count: total })}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onChange(EMPTY_FILTERS)
                  setOpen(false)
                }}
              >
                {t('admin.project.resetFilters')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const INPUT =
  'h-10 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-caption uppercase tracking-wide text-content-muted">
        {label}
      </span>
      {children}
    </label>
  )
}

/**
 * Un `<select>` natif, délibérément.
 *
 * Sur un téléphone il ouvre la roue du système : lisible, utilisable au pouce,
 * et qui ne sort jamais de l'écran. Une liste déroulante maison de quarante
 * personnes déborde du cadre et se manipule mal — c'est exactement l'écueil
 * qu'on cherche à éviter ici.
 */
function Select({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  options: { value: string; label: string }[]
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={INPUT}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}
